// Client-side asset resolver. Fetches /assets/manifest.json once at boot and
// caches it. Every panel that shows an image routes src through assetUrl(),
// which falls back to the SVG placeholder generator when the manifest has no
// entry. See HOO-46 (manifest schema), HOO-51 (placeholder generator),
// HOO-47..50 (per-kind image pipelines).

import {
  MANIFEST_PATH, lookupAsset, emptyManifest,
  type AssetManifest, type AssetKind,
} from "../shared/assets.js";
import { placeholderDataUrl, type PlaceholderKind } from "../shared/placeholders.js";

let cached: AssetManifest = emptyManifest();
let loaded = false;
let loadingPromise: Promise<void> | null = null;

/** Fetch the manifest. Subsequent calls are a no-op. */
export async function loadManifest(): Promise<void> {
  if (loaded) return;
  if (loadingPromise) return loadingPromise;
  loadingPromise = (async () => {
    try {
      const res = await fetch(MANIFEST_PATH, { cache: "force-cache" });
      if (!res.ok) throw new Error(`manifest fetch ${res.status}`);
      cached = await res.json() as AssetManifest;
    } catch (e) {
      console.warn("[asset_loader] manifest load failed, using empty", e);
      cached = emptyManifest();
    }
    loaded = true;
  })();
  await loadingPromise;
}

/** Synchronous URL lookup. Callers must have awaited loadManifest() first. */
export function assetUrl(
  kind: AssetKind, key: string | number, fallback: { name: string; category?: string } | null = null,
): string {
  const hit = lookupAsset(cached, kind, key);
  if (hit) return hit;
  if (!fallback) return placeholderDataUrl({ kind: kind as PlaceholderKind, name: String(key) });
  return placeholderDataUrl({
    kind: kind as PlaceholderKind,
    name: fallback.name,
    category: fallback.category,
  });
}

/** Explicitly replace the in-memory manifest (used by tests). */
export function setManifest(m: AssetManifest): void { cached = m; loaded = true; }
export function getManifest(): AssetManifest { return cached; }
